import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  HiOutlineCog,
  HiOutlineBell,
  HiOutlineShieldCheck,
  HiOutlineGlobe,
  HiOutlineMail,
  HiOutlineDatabase,
  HiCheck,
} from "react-icons/hi";

const AdminSettings = () => {
  const { getProfile } = useAuth();
  const [saved, setSaved] = useState(false);

  const [settings, setSettings] = useState({
    // General
    siteName: "Foodoscope",
    maintenanceMode: false,
    // Notifications
    emailNotifications: true,
    slackNotifications: false,
    notifyOnNewUser: true,
    notifyOnPayment: true,
    notifyOnRateLimit: true,
    // Security
    enforceRateLimit: true,
    maxRequestsPerMinute: 50,
    blockDuration: 24,
    autoBlockAfterViolations: 5,
    // API
    apiTimeout: 30,
    enableCors: true,
    enableLogging: true,
  });

  useEffect(() => {
    // Load settings from localStorage if available
    try {
      const storedSettings = localStorage.getItem("adminSettings");
      if (storedSettings) {
        setSettings((prev) => ({ ...prev, ...JSON.parse(storedSettings) }));
      }
    } catch (err) {
      console.error("Unable to load admin settings:", err);
    }
  }, []);

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    try {
      localStorage.setItem("adminSettings", JSON.stringify(settings));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Unable to save settings:", err);
    }
  };

  const SectionHeader = ({ icon: Icon, title, subtitle }) => (
    <div className="flex items-center gap-3 mb-5">
      <div className="w-9 h-9 rounded-lg bg-[#BDE958]/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#BDE958]" />
      </div>
      <div>
        <h2 className="text-white font-bold text-sm">{title}</h2>
        {subtitle && (
          <p className="text-gray-500 text-xs mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
  );

  const ToggleRow = ({ label, description, checked, onChange }) => (
    <div className="flex items-center justify-between py-3.5 border-b border-white/5 last:border-b-0">
      <div>
        <p className="text-sm text-gray-200">{label}</p>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors ${
          checked ? "bg-[#BDE958]" : "bg-gray-600"
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-gray-400 text-sm mt-1">
            Configure platform preferences and security options
          </p>
        </div>
        <button
          onClick={handleSave}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
            saved
              ? "bg-green-500 text-white"
              : "bg-[#BDE958] hover:bg-[#a8d44a] text-[#161616]"
          }`}
        >
          {saved ? (
            <>
              <HiCheck className="w-4 h-4" /> Saved
            </>
          ) : (
            "Save Settings"
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <div className="bg-[#1E1E1E] rounded-xl border border-white/5 p-6">
          <SectionHeader
            icon={HiOutlineCog}
            title="General"
            subtitle="Platform-wide configuration"
          />
          <div>
            <div className="py-3.5 border-b border-white/5">
              <label className="block text-sm text-gray-200 mb-2">
                Site Name
              </label>
              <input
                type="text"
                value={settings.siteName}
                onChange={(e) => handleChange("siteName", e.target.value)}
                className="w-full bg-[#161616] border border-white/10 rounded-lg px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#BDE958]/50"
              />
            </div>
            <ToggleRow
              label="Maintenance Mode"
              description="Temporarily disable public access"
              checked={settings.maintenanceMode}
              onChange={(val) => handleChange("maintenanceMode", val)}
            />
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-[#1E1E1E] rounded-xl border border-white/5 p-6">
          <SectionHeader
            icon={HiOutlineBell}
            title="Notifications"
            subtitle="Alert preferences"
          />
          <div>
            <ToggleRow
              label="Email Notifications"
              description="Receive alerts via email"
              checked={settings.emailNotifications}
              onChange={(val) => handleChange("emailNotifications", val)}
            />
            <ToggleRow
              label="New User Registration"
              checked={settings.notifyOnNewUser}
              onChange={(val) => handleChange("notifyOnNewUser", val)}
            />
            <ToggleRow
              label="Payment Received"
              checked={settings.notifyOnPayment}
              onChange={(val) => handleChange("notifyOnPayment", val)}
            />
            <ToggleRow
              label="Rate Limit Violations"
              checked={settings.notifyOnRateLimit}
              onChange={(val) => handleChange("notifyOnRateLimit", val)}
            />
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-[#1E1E1E] rounded-xl border border-white/5 p-6">
          <SectionHeader
            icon={HiOutlineShieldCheck}
            title="Security"
            subtitle="Rate limiting and access control"
          />
          <div>
            <ToggleRow
              label="Enforce Rate Limiting"
              description="Automatically limit excessive requests"
              checked={settings.enforceRateLimit}
              onChange={(val) => handleChange("enforceRateLimit", val)}
            />
            <div className="py-3.5 border-b border-white/5">
              <label className="block text-sm text-gray-200 mb-2">
                Max Requests / Minute
              </label>
              <input
                type="number"
                value={settings.maxRequestsPerMinute}
                onChange={(e) =>
                  handleChange(
                    "maxRequestsPerMinute",
                    Number(e.target.value) || 0
                  )
                }
                className="w-full bg-[#161616] border border-white/10 rounded-lg px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#BDE958]/50"
              />
            </div>
            <div className="py-3.5 border-b border-white/5">
              <label className="block text-sm text-gray-200 mb-2">
                Block Duration (hours)
              </label>
              <input
                type="number"
                value={settings.blockDuration}
                onChange={(e) =>
                  handleChange("blockDuration", Number(e.target.value) || 0)
                }
                className="w-full bg-[#161616] border border-white/10 rounded-lg px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#BDE958]/50"
              />
            </div>
            <div className="py-3.5">
              <label className="block text-sm text-gray-200 mb-2">
                Auto-block After Violations
              </label>
              <input
                type="number"
                value={settings.autoBlockAfterViolations}
                onChange={(e) =>
                  handleChange(
                    "autoBlockAfterViolations",
                    Number(e.target.value) || 0
                  )
                }
                className="w-full bg-[#161616] border border-white/10 rounded-lg px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#BDE958]/50"
              />
            </div>
          </div>
        </div>

        {/* API Settings */}
        <div className="bg-[#1E1E1E] rounded-xl border border-white/5 p-6">
          <SectionHeader
            icon={HiOutlineDatabase}
            title="API Configuration"
            subtitle="Request handling and logging"
          />
          <div>
            <div className="py-3.5 border-b border-white/5">
              <label className="block text-sm text-gray-200 mb-2">
                API Timeout (seconds)
              </label>
              <input
                type="number"
                value={settings.apiTimeout}
                onChange={(e) =>
                  handleChange("apiTimeout", Number(e.target.value) || 0)
                }
                className="w-full bg-[#161616] border border-white/10 rounded-lg px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#BDE958]/50"
              />
            </div>
            <ToggleRow
              label="Enable CORS"
              description="Allow cross-origin requests"
              checked={settings.enableCors}
              onChange={(val) => handleChange("enableCors", val)}
            />
            <ToggleRow
              label="Enable Request Logging"
              description="Log all API requests for auditing"
              checked={settings.enableLogging}
              onChange={(val) => handleChange("enableLogging", val)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
